import { use, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { ChatContext } from '@app/context'
import { useConfiguration, useUserCulture } from '@app/hooks'

import {
  getAppUrl,
  getImageUrl,
  getListingPriceFormatted,
  getUserCurrency,
  isB2BView,
} from '@app/utils'

import { getOriginalData } from '@lib/utils'

export const useDialogListingCard = (listingId: number) => {
  const { state } = use(ChatContext)
  const { t } = useTranslation()

  const culture = useUserCulture()

  const listing = state.listings.get(listingId)

  const listingImage = useMemo(() => {
    return listing?.resources?.pictures?.items[0]?.url || getImageUrl()
  }, [listing])

  const listingTitle = useMemo(() => {
    return (
      getOriginalData(listing?.title || listing?.name) ||
      listing?.automaticTitle
    )
  }, [listing])

  const listingPrice = useMemo(() => {
    if (listing?.price?.display) {
      return listing.price.display
    }

    const automaticDisplay = getOriginalData(listing?.price?.automaticDisplay)
    if (automaticDisplay) {
      return automaticDisplay
    }

    const isPriceOnDemand =
      listing?.price?.values &&
      listing.price.values.length > 0 &&
      listing.price.values[0].value === undefined

    if (isPriceOnDemand) {
      return t('Chat.PriceOnDemand')
    }

    if (listing?.price?.values) {
      const price =
        listing.price.values.find((x) => x.currencyId === getUserCurrency()) ||
        listing.price.values[0]
      return getListingPriceFormatted({
        currencyId: price.currencyId,
        amount: price.value,
        currentLocale: culture,
        signDisplay: 'never',
      })
    }
    return ''
  }, [
    culture,
    listing?.price?.automaticDisplay,
    listing?.price?.display,
    listing?.price?.values,
  ])

  const configuration = useConfiguration()

  const publicationLink = useMemo(() => {
    if (!listing) return

    const isProject =
      listing.class == 'ProjectAsListing' || listing.class == 'ProjectListing'
    const listingType = (isProject && 'project') || 'listing'

    const path = isB2BView()
      ? `${listingType}s/${listing.id}/publication`
      : `${listingType}/${listing.id}`

    const baseUrl = getAppUrl(configuration, '')
    return `${baseUrl}/${path}`
  }, [configuration, listing])

  return {
    listing,
    listingImage,
    listingPrice,
    listingTitle,
    publicationLink,
  }
}
